import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "../store";

import FormsService from "../services/FormsService";

export interface FormsInitialState {
  forms: IFormsState;
}

export interface IFormsState {
  loading: boolean;
  forms: IForm[] | null;
  form: IForm | null;
  me: ITempUser | null;
}

export interface ITempUser {
  name: string;
  service: string;
  patient: string;
}

export interface IForm {
  title: string;
  formTemplateJson: any;
  formVersionId: string;
  formIdentifier: string;
  progress: string;
  completedDateTimeUtc: Date;
  responseId?: string;
}

export interface IFormTaker {
  loading: boolean;
  id?: string;
  formVersionId?: string;
  answers: IAnswerModel[];
  completed:boolean;
}

export interface IFormSubmitting {
  processing: boolean;
  ok?: boolean;
}

export interface IAnswerModel {
  fieldName: string;
  answer: string;
  answer2: string | null;
}

const initialState: IFormsState = {
  loading: false,
  forms: null,
  form: null,
  me: null,
};

const fetchForms = createAsyncThunk(
  "forms/get",
  async ({ inviteeId }: any, { rejectWithValue }) => {
    try {
      let res;
      if (inviteeId) {
        res = await FormsService.getInviteeForms(inviteeId);
      } else {
        res = await FormsService.getForms();
      }
      return res.data;
    } catch (err: any) {
      if (err.response.status === 404) {
        return null;
      }

      return rejectWithValue(err);
    }
  }
);

const fetchUser = createAsyncThunk(
  "user/get",
  async ({ inviteeId }: any, { rejectWithValue }) => {
    try {
      const res = await FormsService.getUser(inviteeId);

      return res.data;
    } catch (err: any) {
      if (err.response.status === 404) {
        return null;
      }

      return rejectWithValue(err);
    }
  }
);

const fetchForm = createAsyncThunk(
  "forms/byid",
  async (formId: string, { rejectWithValue }) => {
    try {
      const res = await FormsService.getForm(formId);
      return res.data;
    } catch (err: any) {
      if (err.response.status === 404) {
        return null;
      }

      return rejectWithValue(err);
    }
  }
);

export interface IAnswerRequestModel {
  formVersionId: string;
  responseId: string;
  answer: IAnswerModel;
}

export interface IAnswersRequestModel {
  formVersionId: string;
  responseId: string;
  answers: IAnswerModel[];
}

const fetchFormsReducerHandler = (
  actionState: string,
  state: IFormsState,
  action?: PayloadAction<IForm[] | null>
) => {
  switch (actionState) {
    case "pending":
      state.loading = true;
      break;
    case "fulfilled":
      state.loading = false;
      state.forms = action?.payload!;
      break;
    case "rejected":
      state.loading = false;
      break;
  }
};

const fetchFormReducerHandler = (
  actionState: string,
  state: IFormsState,
  action?: PayloadAction<IForm | null>
) => {
  switch (actionState) {
    case "pending":
      state.loading = true;
      break;
    case "fulfilled":
      state.loading = false;
      state.form = action?.payload!;
      break;
    case "rejected":
      state.loading = false;
      break;
  }
};


export const formsSlice = createSlice({
  name: "forms",
  initialState,
  reducers: {
    resetFormState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(
        fetchUser.fulfilled,
        (state: IFormsState, action: PayloadAction<ITempUser | null>) => {
          state.me = action.payload;
        }
      )
      .addCase(fetchForms.pending, (state: IFormsState) =>
        fetchFormsReducerHandler("pending", state)
      )

      .addCase(
        fetchForms.fulfilled,
        (state: IFormsState, action: PayloadAction<IForm[] | null>) =>
          fetchFormsReducerHandler("fulfilled", state, action)
      )
      .addCase(fetchForms.rejected, (state) =>
        fetchFormsReducerHandler("rejected", state)
      )
      .addCase(fetchForm.pending, (state: IFormsState) =>
        fetchFormReducerHandler("pending", state)
      )
      .addCase(
        fetchForm.fulfilled,
        (state: IFormsState, action: PayloadAction<IForm | null>) =>
          fetchFormReducerHandler("fulfilled", state, action)
      )
      .addCase(fetchForm.rejected, (state) =>
        fetchFormReducerHandler("rejected", state)
      );
  },
});

const getForms = (state: RootState) => state.forms;
const getFormUser = (state: RootState) => state.forms.me;

export {
  fetchForm,
  fetchForms,
  fetchUser,
  getForms,
  getFormUser
};

export const { resetFormState } = formsSlice.actions;

export default formsSlice.reducer;
